// Accessibility helpers per JIS X 8341-3 (Japanese WCAG 2.1
// conformance) + WCAG 2.2 AA.
//
// What this module exposes:
//   * usePrefersReducedMotion — honour OS reduce-motion setting
//   * useHighContrast — toggle high-contrast theme + persist
//   * announce — programmatic live-region announcer (sr-only)
//   * VoiceInputCapable — feature-detect Web Speech API
//   * focusFirstHeading / focusByRouteChange — keyboard nav on
//     route transitions so screen readers land in the right place

import { useEffect, useState } from "react";


// ── Motion preference ────────────────────────────────────────────────


export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  return reduced;
}


// ── High-contrast theme ──────────────────────────────────────────────


const HC_STORAGE_KEY = "lifestyle.high_contrast.v1";


export function useHighContrast(): {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
} {
  const [enabled, setEnabledState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(HC_STORAGE_KEY) === "1";
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute(
      "data-high-contrast", enabled ? "true" : "false",
    );
  }, [enabled]);

  return {
    enabled,
    setEnabled: (v: boolean) => {
      setEnabledState(v);
      try {
        window.localStorage.setItem(HC_STORAGE_KEY, v ? "1" : "0");
      } catch {
        // SSR / storage quota
      }
    },
  };
}


// ── Live-region announcer ────────────────────────────────────────────


let liveRegion: HTMLDivElement | null = null;


function ensureLiveRegion(): HTMLDivElement {
  if (typeof document === "undefined") {
    throw new Error("announce only available in browser");
  }
  if (liveRegion) return liveRegion;
  const div = document.createElement("div");
  div.setAttribute("role", "status");
  div.setAttribute("aria-live", "polite");
  div.setAttribute("aria-atomic", "true");
  div.style.position = "absolute";
  div.style.width = "1px";
  div.style.height = "1px";
  div.style.padding = "0";
  div.style.margin = "-1px";
  div.style.overflow = "hidden";
  div.style.clip = "rect(0, 0, 0, 0)";
  div.style.whiteSpace = "nowrap";
  div.style.border = "0";
  document.body.appendChild(div);
  liveRegion = div;
  return div;
}


/** Push a message to the assertive live region for screen readers. */
export function announce(message: string, urgent = false): void {
  if (typeof document === "undefined") return;
  const region = ensureLiveRegion();
  region.setAttribute("aria-live", urgent ? "assertive" : "polite");
  // Clearing + re-setting forces AT to re-read identical strings
  region.textContent = "";
  // Defer to next tick so the clear is observed
  setTimeout(() => {
    region.textContent = message;
  }, 50);
}


// ── Voice input ──────────────────────────────────────────────────────


export function isVoiceInputCapable(): boolean {
  if (typeof window === "undefined") return false;
  return !!(
    (window as any).SpeechRecognition ||
    (window as any).webkitSpeechRecognition
  );
}


// ── Focus management ─────────────────────────────────────────────────


/** On route change, focus the first heading in main so screen readers
 *  land on the new surface's title. */
export function focusFirstHeading(): void {
  if (typeof document === "undefined") return;
  const main = document.querySelector("main") as HTMLElement | null;
  if (!main) return;
  const h = main.querySelector("h1, h2") as HTMLElement | null;
  if (!h) return;
  if (!h.hasAttribute("tabindex")) {
    h.setAttribute("tabindex", "-1");
  }
  h.focus();
}


// ── Skip link ────────────────────────────────────────────────────────


export const SKIP_LINK_CLASSES =
  "sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 " +
  "focus:bg-(--color-accent-gold) focus:text-stone-900 focus:px-3 " +
  "focus:py-1 focus:rounded focus:z-50 focus:font-medium";
